import { CommonModule } from '@angular/common';
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { fromEvent, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { HelpWalkthroughStep, WalkthroughRunState } from '../models/help-api.types';
import { HelpTargetRegistryService } from '../services/help-target-registry.service';
import { HelpWalkthroughRunnerService } from '../services/help-walkthrough-runner.service';
import { isHelpTargetElementVisible } from '../utils/help-target-visibility';
import { resolveHelpTargetElement } from '../utils/resolve-help-target-element';

@Component({
  selector: 'app-help-walkthrough-overlay',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './help-walkthrough-overlay.component.html',
  styleUrls: ['./help-walkthrough-overlay.component.scss'],
})
export class HelpWalkthroughOverlayComponent implements OnInit, OnDestroy {
  state: WalkthroughRunState | null = null;
  ringStyle: Record<string, string> = { display: 'none' };
  cardStyle: Record<string, string> = {};
  pinStyle: Record<string, string> = { display: 'none' };
  currentStep: HelpWalkthroughStep | null = null;

  private readonly destroy$ = new Subject<void>();
  /** Single frame after scroll / tab switch so getBoundingClientRect matches paint. */
  private layoutRetryRaf = 0;
  private bootstrapTabRevealTried = false;
  private skippingMissingDomSteps = false;
  private readonly minTargetPx = 2;

  constructor(
    private readonly runner: HelpWalkthroughRunnerService,
    private readonly registry: HelpTargetRegistryService
  ) {}

  ngOnInit(): void {
    this.runner.runState$.pipe(takeUntil(this.destroy$)).subscribe(s => {
      this.state = s;
      this.currentStep = s ? s.steps[s.index] : null;
      if (!this.skippingMissingDomSteps) {
        this.scheduleLayout();
      }
    });

    fromEvent(window, 'resize')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.scheduleLayout());

    fromEvent(window, 'scroll', { capture: true })
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.scheduleLayout());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.cancelLayoutJobs();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.state) {
      this.runner.exit();
    }
  }

  /** Back skips steps whose target_key does not resolve in the DOM; disabled when no earlier resolvable step exists. */
  get canGoBack(): boolean {
    const s = this.state;
    if (!s || s.index <= 0) {
      return false;
    }
    return this.findPreviousResolvableIndex(s) !== null;
  }

  get isLast(): boolean {
    return !!this.state && this.state.index >= this.state.steps.length - 1;
  }

  next(): void {
    this.runner.next();
  }

  back(): void {
    const s = this.runner.snapshot;
    if (!s || s.index <= 0) {
      return;
    }
    const targetIndex = this.findPreviousResolvableIndex(s);
    if (targetIndex === null) {
      return;
    }
    this.skippingMissingDomSteps = true;
    try {
      while (this.runner.snapshot && this.runner.snapshot.index > targetIndex) {
        this.runner.back();
      }
    } finally {
      this.skippingMissingDomSteps = false;
      this.scheduleLayout();
    }
  }

  exit(): void {
    this.runner.exit();
  }

  finish(): void {
    this.runner.finish();
  }

  trackByIndex(index: number): number {
    return index;
  }

  private cancelLayoutJobs(): void {
    if (this.layoutRetryRaf) {
      cancelAnimationFrame(this.layoutRetryRaf);
      this.layoutRetryRaf = 0;
    }
  }

  private scheduleLayout(): void {
    this.cancelLayoutJobs();
    this.bootstrapTabRevealTried = false;
    this.layoutAttemptChain();
  }

  /** Resolve element from registry first, then `[data-help-target]` as fallback. */
  private resolveTargetElement(targetKey: string): HTMLElement | null {
    return resolveHelpTargetElement(this.registry, targetKey);
  }

  /** Largest index below the current step whose target is present in the registry or DOM. */
  private findPreviousResolvableIndex(s: WalkthroughRunState): number | null {
    for (let i = s.index - 1; i >= 0; i--) {
      const step = s.steps[i];
      if (step && this.resolveTargetElement(step.target_key)) {
        return i;
      }
    }
    return null;
  }

  /**
   * One paint after scroll (and optionally one after revealing a Bootstrap tab) so rects are stable.
   * Missing or unusable targets skip forward immediately — no multi-frame retry backlog.
   */
  private layoutAttemptChain(): void {
    if (!this.state || !this.currentStep) {
      this.clearSpotlight();
      return;
    }

    const step = this.currentStep;
    const targetKey = step.target_key;

    let resolved = this.resolveTargetElement(targetKey);
    if (!resolved) {
      this.advancePastUnavailableStep();
      return;
    }

    resolved.scrollIntoView({ block: 'nearest', inline: 'nearest' });

    this.layoutRetryRaf = requestAnimationFrame(() => {
      this.layoutRetryRaf = 0;

      if (!this.state || !this.currentStep || this.currentStep.target_key !== targetKey) {
        return;
      }

      resolved = this.resolveTargetElement(targetKey);
      if (!resolved) {
        this.advancePastUnavailableStep();
        return;
      }

      let r = resolved.getBoundingClientRect();

      if (
        r.width < this.minTargetPx &&
        r.height < this.minTargetPx &&
        !this.bootstrapTabRevealTried
      ) {
        this.bootstrapTabRevealTried = true;
        if (this.tryRevealBootstrapTabPane(resolved)) {
          this.layoutRetryRaf = requestAnimationFrame(() => {
            this.layoutRetryRaf = 0;
            if (!this.state || !this.currentStep || this.currentStep.target_key !== targetKey) {
              return;
            }
            resolved = this.resolveTargetElement(targetKey);
            if (!resolved) {
              this.advancePastUnavailableStep();
              return;
            }
            r = resolved.getBoundingClientRect();
            this.finalizeLayoutOrSkip(resolved, r, step, targetKey);
          });
          return;
        }
      }

      this.finalizeLayoutOrSkip(resolved, r, step, targetKey);
    });
  }

  private finalizeLayoutOrSkip(
    resolved: HTMLElement,
    r: DOMRect,
    step: HelpWalkthroughStep,
    targetKey: string
  ): void {
    if (!this.state || !this.currentStep || this.currentStep.target_key !== targetKey) {
      return;
    }

    const ok =
      isHelpTargetElementVisible(resolved) &&
      r.width >= this.minTargetPx &&
      r.height >= this.minTargetPx &&
      resolved.getClientRects().length > 0;

    if (ok) {
      this.applySpotlight(r, step.placement);
      return;
    }

    this.advancePastUnavailableStep();
  }

  private clearSpotlight(): void {
    this.ringStyle = { display: 'none' };
    this.pinStyle = { display: 'none' };
    this.cardStyle = {};
  }

  /**
   * If the target lives in a Bootstrap tab panel that is not `.show`, click the matching tab control.
   */
  private tryRevealBootstrapTabPane(targetEl: HTMLElement): boolean {
    const pane = targetEl.closest('.tab-pane');
    if (!(pane instanceof HTMLElement) || pane.classList.contains('show')) {
      return false;
    }
    const id = pane.id;
    if (!id) {
      return false;
    }
    try {
      const esc =
        typeof CSS !== 'undefined' && typeof CSS.escape === 'function' ? CSS.escape(id) : id.replace(/"/g, '\\"');
      const btn =
        (document.querySelector(`[data-bs-target="#${esc}"]`) as HTMLElement | null) ||
        (document.querySelector(`a[href="#${esc}"]`) as HTMLElement | null);
      if (btn) {
        btn.click();
        return true;
      }
    } catch {
      /* invalid selector */
    }
    return false;
  }

  private applySpotlight(r: DOMRect, placement: HelpWalkthroughStep['placement']): void {
    const pad = 8;
    this.ringStyle = {
      display: 'block',
      left: `${r.left - pad}px`,
      top: `${r.top - pad}px`,
      width: `${r.width + pad * 2}px`,
      height: `${r.height + pad * 2}px`,
    };
    this.pinStyle = {
      display: 'flex',
      left: `${r.left + r.width / 2 - 14}px`,
      top: `${r.top - pad - 22}px`,
    };
    this.positionCard(r, placement);
  }

  /**
   * After layout retries, no usable anchor: skip this step. {@link HelpWalkthroughRunnerService#next}
   * finishes the tour on the last step.
   * If the next step(s) have no DOM target at all, advance until one resolves or the tour ends.
   */
  private advancePastUnavailableStep(): void {
    this.clearSpotlight();
    this.skippingMissingDomSteps = true;
    try {
      this.runner.next();
      while (true) {
        const s = this.runner.snapshot;
        if (!s?.steps?.length) {
          return;
        }
        const step = s.steps[s.index];
        if (!step) {
          this.runner.finish();
          return;
        }
        if (this.resolveTargetElement(step.target_key)) {
          break;
        }
        if (s.index >= s.steps.length - 1) {
          this.runner.finish();
          return;
        }
        this.runner.next();
      }
    } finally {
      this.skippingMissingDomSteps = false;
      this.scheduleLayout();
    }
  }

  private positionCard(target: DOMRect, placement: HelpWalkthroughStep['placement']): void {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const margin = 16;
    const gap = 14;
    const cardW = Math.min(380, vw - margin * 2);
    const estH = 200;

    let left = target.left + target.width / 2 - cardW / 2;
    let top = target.bottom + gap;
    let transform = '';

    const resolved = placement === 'auto' ? this.resolveAutoPlacement(target, cardW, estH, gap) : placement;

    switch (resolved) {
      case 'top':
        top = target.top - gap - estH;
        left = target.left + target.width / 2 - cardW / 2;
        break;
      case 'left':
        left = target.left - cardW - gap;
        top = target.top + target.height / 2 - estH / 2;
        break;
      case 'right':
        left = target.right + gap;
        top = target.top + target.height / 2 - estH / 2;
        break;
      case 'bottom':
        top = target.bottom + gap;
        left = target.left + target.width / 2 - cardW / 2;
        break;
      default:
        break;
    }

    left = Math.min(Math.max(margin, left), vw - cardW - margin);
    top = Math.min(Math.max(margin, top), vh - estH - margin);

    this.cardStyle = {
      left: `${left}px`,
      top: `${top}px`,
      width: `${cardW}px`,
      transform,
    };
  }

  private resolveAutoPlacement(target: DOMRect, cardW: number, estH: number, gap: number): 'top' | 'bottom' | 'left' | 'right' {
    const spaceBelow = window.innerHeight - target.bottom - gap;
    const spaceAbove = target.top - gap;
    if (spaceBelow >= estH) {
      return 'bottom';
    }
    if (spaceAbove >= estH) {
      return 'top';
    }
    const spaceRight = window.innerWidth - target.right - gap;
    if (spaceRight >= cardW) {
      return 'right';
    }
    return 'left';
  }
}
